import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, Package, Truck, CheckCircle, Clock, XCircle, RotateCcw, Mail, Phone, RefreshCw } from 'lucide-react';
import orderService from '../services/order.service';
import { sanitizeError } from '../utils/errorUtils';
import { generateInvoice } from '../utils/generateInvoice';
import { Printer } from 'lucide-react';

const ReserveOrders = () => {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        deliveryPartner: '',
        search: ''
    });
    useEffect(() => {
        fetchOrders();
    }, [filters]);

    const fetchOrders = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await orderService.getReverseEligibleOrders(filters);
            if (response.success) {
                setOrders(response.data.orders || []);
            }
        } catch (err) {
            setError(sanitizeError(err.response?.data?.message || 'Failed to fetch orders'));
        } finally {
            setLoading(false);
        }
    };

    const handleReverseOrder = (order) => {
        navigate(`/create-order?reverseFrom=${order._id}`);
    };

    const handleViewOrder = (orderId) => {
        navigate(`/order-details?id=${orderId}`);
    };

    const handlePrintInvoice = (e, order) => {
        e.stopPropagation();
        generateInvoice(order);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'delivered':
                return <CheckCircle className="w-4 h-4 text-green-600" />;
            case 'picked_up':
            case 'in_transit':
            case 'out_for_delivery':
                return <Truck className="w-4 h-4 text-blue-600" />;
            case 'pending':
            case 'confirmed':
            case 'reverse_pending':
                return <Clock className="w-4 h-4 text-yellow-600" />;
            case 'cancelled':
            case 'lost':
            case 'damaged':
                return <XCircle className="w-4 h-4 text-red-600" />;
            case 'rto':
            case 'rto_delivered':
                return <RotateCcw className="w-4 h-4 text-orange-600" />;
            default:
                return <Package className="w-4 h-4 text-gray-600" />;
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'delivered':
                return 'bg-green-100 text-green-700';
            case 'picked_up':
            case 'in_transit':
            case 'out_for_delivery':
                return 'bg-blue-100 text-blue-700';
            case 'pending':
            case 'confirmed':
            case 'reverse_pending':
                return 'bg-yellow-100 text-yellow-700';
            case 'cancelled':
            case 'lost':
            case 'damaged':
                return 'bg-red-100 text-red-700';
            case 'rto':
            case 'rto_delivered':
                return 'bg-orange-100 text-orange-700';
            default:
                return 'bg-gray-100 text-gray-700';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Empty State
    if (!loading && orders.length === 0 && !filters.search) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-50 min-h-[calc(100vh-96px)]">
                <div className="text-center">
                    <img src="/images/icon/box-icon.png" alt="box-icon" className='m-auto'/>
                    <h2 className="mt-9 text-xl font-bold text-gray-800">
                        No delivered orders available for reversal
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                        Only delivered forward orders can be reversed. Complete a delivery first to create a return shipment.
                    </p>
                </div>
            </div>
        );
    }

    // Orders List View
    return (
        <div className="min-h-screen font-sans text-[#1a2b4b]">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
                <h1 className="text-2xl font-bold">Reverse Orders (Domestic)</h1>
                <div className="flex gap-2">
                    <a href="tel:+918826885557" className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-blue-600 transition">
                        <Phone className="w-4 h-4" />
                        +918826885557
                    </a>
                    <a
                        href="mailto:support@flywelllogistics.com"
                        className="flex items-center px-3 py-1.5 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition">
                        <Mail className="w-4 h-4 mr-2" />
                        Email Us
                    </a>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                    {error}
                </div>
            )}
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-3">
                <div className="relative flex items-center min-w-[280px] border border-gray-300 rounded-lg bg-white">
                    <Search className="absolute left-3 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        placeholder="Search by order number or AWB"
                        value={filters.search}
                        onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                        className="w-full pl-10 pr-4 py-1.5 text-sm focus:outline-none rounded-lg"
                    />
                </div>

                <select
                    value={filters.deliveryPartner}
                    onChange={(e) => setFilters({ ...filters, deliveryPartner: e.target.value })}
                    className="px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-bold text-gray-700 bg-[#f1f4f9] min-w-[160px]"
                >
                    <option value="">All Couriers</option>
                    <option value="delhivery">Delhivery</option>
                    <option value="nimbuspost">NimbusPost</option>
                    <option value="skart">Skart (International)</option>
                </select>
            </div>

            {/* Orders Table */}
            <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm">
                {loading ? (
                    <div className="flex justify-center items-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    </div>
                ) : orders.length === 0 ? (
                    <div className="text-center py-12">
                        <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500 text-sm">No orders found</p>
                    </div>
                ) : (
                    <table className="w-full text-left border-collapse">
                        <thead className="bg-[#e9ecef] text-[11px] uppercase text-gray-700 font-extrabold border-b border-gray-200">
                            <tr>
                                <th className="px-6 py-2">Order Number</th>
                                <th className="px-6 py-2">AWB</th>
                                <th className="px-6 py-2">Customer</th>
                                <th className="px-6 py-2">Destination</th>
                                <th className="px-6 py-2">Courier</th>
                                <th className="px-6 py-2">Status</th>
                                <th className="px-6 py-2">Amount</th>
                                <th className="px-6 py-2">Date</th>
                                <th className="px-6 py-2">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="text-[13px]">
                            {orders.map((order) => (
                                <tr key={order._id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/50 transition-colors">
                                    <td className="px-6 py-3 font-bold text-blue-600">
                                        {order.orderNumber}
                                    </td>
                                    <td className="px-6 py-3 font-medium text-gray-700">
                                        {order.awb || '-'}
                                    </td>
                                    <td className="px-6 py-3">
                                        <div className="font-medium text-gray-800">{order.deliveryDetails?.name}</div>
                                        <div className="text-xs text-gray-500">{order.deliveryDetails?.phone}</div>
                                    </td>
                                    <td className="px-6 py-3">
                                        <div className="font-medium text-gray-800">{order.deliveryDetails?.city}</div>
                                        <div className="text-xs text-gray-500">{order.deliveryDetails?.pincode}</div>
                                    </td>
                                    <td className="px-6 py-3 font-medium text-gray-700 capitalize">
                                        {order.metadata?.courierName ||
                                         order.metadata?.nimbusCourierName ||
                                         order.metadata?.skartCourierName ||
                                         order.deliveryPartner?.replace('_', ' ')}
                                    </td>
                                    <td className="px-6 py-3">
                                        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(order.status)}`}>
                                            {getStatusIcon(order.status)}
                                            {order.status.replace(/_/g, ' ').toUpperCase()}
                                        </span>
                                    </td>
                                    <td className="px-6 py-3 font-bold text-gray-800">
                                        {formatAmount(order.pricing?.totalAmount)}
                                    </td>
                                    <td className="px-6 py-3 text-gray-600">
                                        {formatDate(order.createdAt)}
                                    </td>
                                    <td className="px-6 py-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() => handleViewOrder(order._id)}
                                                className="inline-flex items-center gap-1 text-xs font-bold text-blue-600 hover:text-blue-700 transition">
                                                View
                                            </button>
                                            {order.isReversed ? (
                                                <span className="inline-flex items-center gap-1 text-xs font-bold text-gray-400 cursor-not-allowed">
                                                    <CheckCircle className="w-3 h-3" />
                                                    Reversed
                                                </span>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleReverseOrder(order)}
                                                    className="inline-flex items-center gap-1 text-xs font-bold text-purple-600 hover:text-purple-700 transition">
                                                    <RefreshCw className="w-3 h-3" />
                                                    Reverse
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                onClick={(e) => handlePrintInvoice(e, order)}
                                                className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-900 transition"
                                                title="Print tax invoice"
                                            >
                                                <Printer className="w-3 h-3" />
                                                Invoice
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Orders Count */}
            {!loading && orders.length > 0 && (
                <div className="mt-4 text-sm text-gray-600">
                    Showing {orders.length} order{orders.length !== 1 ? 's' : ''}
                </div>
            )}
        </div>
    );
};

export default ReserveOrders;
